import { useState, useRef } from "react";
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Upload, Loader2, Crop as CropIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  RetroDialog,
  RetroDialogHeader,
  RetroDialogBody,
  RetroDialogFooter,
} from "@/components/ui/retro-dialog";

interface PlaylistCoverCropModalProps {
  open: boolean;
  onClose: () => void;
  playlistId: string;
  userId: string;
  onCoverUpdated: (url: string) => void;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      { unit: "%", width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default function PlaylistCoverCropModal({
  open,
  onClose,
  playlistId,
  userId,
  onCoverUpdated,
}: PlaylistCoverCropModalProps) {
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  const handleUpload = async () => {
    if (!imgRef.current || !completedCrop) return;

    setUploading(true);
    try {
      const image = imgRef.current;
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      canvas.width = 1200;
      canvas.height = 1200;
      const ctx = canvas.getContext("2d")!;

      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        1200,
        1200
      );

      const blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), "image/png", 1)
      );

      const filePath = `${userId}/${playlistId}-custom-${Date.now()}.png`;

      const { error: uploadErr } = await supabase.storage
        .from("playlist-covers")
        .upload(filePath, blob, { contentType: "image/png", upsert: true });

      if (uploadErr) throw uploadErr;

      const { data: { publicUrl } } = supabase.storage
        .from("playlist-covers")
        .getPublicUrl(filePath);

      await supabase
        .from("playlists")
        .update({ cover_image_url: publicUrl } as any)
        .eq("id", playlistId);

      onCoverUpdated(publicUrl);
      toast.success("Cover image uploaded!");
      onClose();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload cover image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <RetroDialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <RetroDialogHeader
        title={
          <span className="flex items-center gap-2">
            <CropIcon className="w-5 h-5 text-primary" />
            Upload Cover Image
          </span>
        }
      />
      <RetroDialogBody className="max-h-[65vh] overflow-y-auto">
        {!imgSrc ? (
          <label className="flex flex-col items-center justify-center w-full h-48 border-[3px] border-dashed border-foreground rounded-md cursor-pointer hover:bg-muted/50 transition-colors">
            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
            <span className="font-mono text-xs text-muted-foreground">
              Click to select an image
            </span>
            <span className="font-mono text-[10px] text-muted-foreground/60 mt-1">
              JPG, PNG, or WebP
            </span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onSelectFile}
              className="hidden"
            />
          </label>
        ) : (
          <div className="space-y-3">
            <p className="font-mono text-[11px] text-muted-foreground">
              Drag the square to select the area for your cover
            </p>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              className="max-h-[50vh]"
            >
              <img
                ref={imgRef}
                alt="Upload"
                src={imgSrc}
                onLoad={onImageLoad}
                className="max-h-[50vh] w-auto"
              />
            </ReactCrop>
          </div>
        )}
      </RetroDialogBody>
      <RetroDialogFooter>
        <button onClick={onClose} className="btn-comic-ghost">
          Cancel
        </button>
        {imgSrc && (
          <>
            <button
              onClick={() => {
                setImgSrc("");
                setCrop(undefined);
                setCompletedCrop(undefined);
              }}
              className="btn-comic-secondary"
            >
              Choose Different
            </button>
            <button
              onClick={handleUpload}
              disabled={!completedCrop || uploading}
              className="btn-comic-primary disabled:opacity-50"
            >
              {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save Cover
            </button>
          </>
        )}
      </RetroDialogFooter>
    </RetroDialog>
  );
}
